import { HttpClient, HttpContext } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { ERROR_INTERCEPTOR_BYPASS } from '@helios/utils'
import { Observable, of } from 'rxjs'
import { map } from 'rxjs/operators'
import { RootIdType } from '../models'
import {
  AccountClient,
  AccountType,
  AccountTypeGetResponse,
  UltimateParentClientGetResponse,
} from '../models/account.model'

export const ACCOUNTS_URL = `helios-accounts`

/** `fetchSubaccounts` fires for a call the user never asked for, so its two EXPECTED outcomes
 *  degrade silently rather than raising a user-visible error: 403 when the caller has no access
 *  to the account, and 404 until helios ships the route (or for an id it does not know).
 *  Everything else — a 5xx above all — still reports, so a regression on this endpoint shows up
 *  instead of becoming an invisible loss of the rollup. */
const FETCH_SUBACCOUNTS_BYPASS_STATUSES = [403, 404]

@Injectable({ providedIn: 'root' })
export class HttpAccountService {
  constructor(private readonly http: HttpClient) {}

  getAccountUltimateParentClients(
    rootIds: number[],
    rootIdType: RootIdType,
  ): Observable<AccountClient[]> {
    if (rootIds.length === 0) {
      return of([])
    }

    const filterName = `${rootIdType}Id`
    const request = {
      endRow: 10000,
      startRow: 0,
      sort: [],
      group: {
        keys: [],
        fields: [],
      },
      fields: ['parentClientId', 'accountId', 'parentClientName'],
      filter: {
        [filterName]: {
          operator: 'AND',
          filterType: 'number',
          filters: [{ operator: 'in', values: rootIds }],
        },
      },
    }

    return this.http
      .post<UltimateParentClientGetResponse>(ACCOUNTS_URL, request)
      .pipe(
        map(res =>
          res.data.map(client => ({
            id: client.parentClientId,
            name: client.parentClientName,
            accountId: client.accountId,
          })),
        ),
      )
  }

  getAccountTypeFromBreak(
    accountId: number | undefined,
  ): Observable<AccountType[]> {
    if (!accountId) {
      return of([])
    }

    const request = {
      endRow: 10000,
      startRow: 0,
      sort: [],
      group: {
        keys: [],
        fields: [],
      },
      fields: ['accountId', 'accountType', 'sleeveAggregateId'],
      filter: {
        accountId: {
          operator: 'AND',
          filterType: 'number',
          filters: [{ operator: 'in', values: [accountId] }],
        },
      },
    }

    return this.http.post<AccountTypeGetResponse>(ACCOUNTS_URL, request).pipe(
      map(response =>
        response.data.map(account => ({
          accountId: account.accountId,
          accountType: account.accountType,
          sleeveAggregateId: account.sleeveAggregateId,
        })),
      ),
    )
  }

  /**
   * Subaccount ids under an aggregate. Per the backend contract the response INCLUDES the
   * aggregate's own id — callers wanting only children must filter it out.
   *
   * Bypasses the standard error report (see FETCH_SUBACCOUNTS_BYPASS_STATUSES).
   */
  fetchSubaccounts(accountId: number): Observable<number[]> {
    return this.http
      .get<number[] | null>(`${ACCOUNTS_URL}/${accountId}/subaccounts`, {
        context: new HttpContext().set(
          ERROR_INTERCEPTOR_BYPASS,
          FETCH_SUBACCOUNTS_BYPASS_STATUSES,
        ),
      })
      .pipe(map(response => response ?? []))
  }
}
